import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RELEASE_MODULES } from '@constant/remmi-release-modules';
import { RemmiRelease } from '@models/remmi-release.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { RemmiReleasesService } from '@services/api/remmi-releases.service';
import { ToastrService } from 'ngx-toastr';
import { SidebarModule } from 'primeng/sidebar';
import { Subscription } from 'rxjs';
import { ReleasePillComponent } from '../release-pill/release-pill.component';

const PAGE_SIZE = 20;

/**
 * "What's New" drawer: every published release the user can see, including ones they already
 * ignored or dismissed. Opened from the header profile menu; a row opens the release window,
 * which records a view as usual.
 */
@Component({
  selector: 'app-release-history-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SidebarModule, ReleasePillComponent],
  templateUrl: './release-history-drawer.component.html',
  styleUrls: ['./release-history-drawer.component.scss'],
})
export class ReleaseHistoryDrawerComponent implements OnInit, OnDestroy {
  readonly moduleOptions = RELEASE_MODULES;

  isVisible = false;
  isLoading = false;
  items: RemmiRelease[] = [];
  total = 0;
  page = 1;
  modules: string[] = [];
  search = '';

  private searchTimer: any = null;
  private subscription: Subscription;

  constructor(
    private releasesService: RemmiReleasesService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscription = this.releasesService.historyOpen$.subscribe(isOpen => {
      this.isVisible = isOpen;
      if (isOpen) {
        this.load(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    clearTimeout(this.searchTimer);
  }

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  onHide(): void {
    this.releasesService.closeHistory();
  }

  onSearchChange(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(true), 400);
  }

  onModuleChange(): void {
    this.load(true);
  }

  loadMore(): void {
    this.page++;
    this.load(false);
  }

  /** The window reloads the release and records the view; the dot clears here straight away. */
  open(item: RemmiRelease): void {
    if (item.my_state) {
      item.my_state = { ...item.my_state, action: item.my_state.action === 'not_viewed' ? 'viewed' : item.my_state.action };
    }
    this.releasesService.openWindow({ mode: 'single', releaseId: item._id });
  }

  isNew(item: RemmiRelease): boolean {
    return item.my_state?.action === 'not_viewed';
  }

  trackById(_: number, item: RemmiRelease): string {
    return item._id;
  }

  private load(isReset: boolean): void {
    if (isReset) {
      this.page = 1;
    }
    this.isLoading = true;
    this.releasesService.history({ module: this.modules.join(','), search: this.search.trim(), page: this.page, limit: PAGE_SIZE }).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const rows: RemmiRelease[] = this.releasesService.rowsFrom(res);
        this.items = isReset ? rows : [...this.items, ...rows];
        this.total = this.releasesService.totalFrom(res, this.items.length);
      },
      error: err => {
        this.isLoading = false;
        this.toastr.error(this.releasesService.errorMessage(err, 'Could not load release history.'));
      },
    });
  }
}
